import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Box, Button, HStack, Icon } from '@chakra-ui/react';
import { LuArrowUp } from 'react-icons/lu';
import { Tooltip } from '@/components/ui/tooltip.tsx';
import BundleIcon from '@/icons/BundleIcon.tsx';
import {
	DELAYED_READINESS_SHIMMER_DURATION_MS,
	DURATION_FORM_REVEAL_OPEN_MS,
	EASE_OUT,
} from '@/components/ui/motion.ts';
import { LuBookOpen, LuCircleAlert } from 'react-icons/lu';
import ShimmerOverlay from '@/components/common/ShimmerOverlay.tsx';
import { KeyCap } from '@/components/ui/ShortcutHint.tsx';
import { COMPOSER_SUBMIT_KEYSHORTCUTS, COMPOSER_SUBMIT_SHORTCUT_HINT } from './composerSubmitShortcut.ts';
import {
	COMPOSER_PILL_LABEL_MIN_WIDTH,
	COMPOSER_PILL_SHORT_MIN_WIDTH,
	COMPOSER_SUBMIT_CROSSFADE_MS,
	COMPOSER_SUBMIT_MORPH_SETTLE_MS,
	COMPOSER_SUBMIT_REDUCED_MOTION_FADE_MS,
	COMPOSER_TOOLTIP_CLOSE_DELAY_MS,
	COMPOSER_TOOLTIP_OPEN_DELAY_MS,
	composerRowAtLeast,
} from './composerConstants.ts';
import {
	hasKnowledgeBaseFailure,
	KnowledgeBaseReadinessState,
	type KnowledgeBaseReadinessState as KnowledgeBaseReadinessStateValue,
} from '@/hooks/house/documents/knowledgeBaseReadiness';

/**
 * The add pill's three widths, decided by the action row it sits in: the full verb, the short one,
 * or the arrow alone at the ask button's diameter. Each span is shown for its band only, and the
 * hidden sizer carries the same three so its measured width follows whichever is showing.
 */
const FULL_LABEL_CSS = {
	display: 'none',
	[composerRowAtLeast(COMPOSER_PILL_LABEL_MIN_WIDTH)]: { display: 'inline' },
} as const;
const SHORT_LABEL_CSS = {
	display: 'none',
	[composerRowAtLeast(COMPOSER_PILL_SHORT_MIN_WIDTH)]: { display: 'inline' },
	[composerRowAtLeast(COMPOSER_PILL_LABEL_MIN_WIDTH)]: { display: 'none' },
} as const;
const ARROW_CSS = {
	display: 'inline-flex',
	[composerRowAtLeast(COMPOSER_PILL_SHORT_MIN_WIDTH)]: { display: 'none' },
} as const;

// Round send-button diameter in ask mode; the add pill widens from this. Kept at 36 so the row
// (button + the HStack's 16px py) is 52px tall — i.e. a true pill at the collapsed radius
// (COMPOSER_ADD_BUTTON_SIZE / 2).
const ASK_DIAMETER_PX = 36;
// px5 horizontal padding on the pill (20px each side).
const PILL_PADDING_X_PX = 40;

interface ComposerSubmitButtonProps {
	/** Add mode shows the labelled pill; ask mode shows the round send icon. */
	isAdd: boolean;
	/** Ask mode: whether the query can be submitted. */
	canSubmitQuery: boolean;
	/** Ask-mode tooltip. */
	submitTooltip: React.ReactNode;
	/** Ask mode: KB state shown by the small display-only book marker. */
	askReadinessState?: KnowledgeBaseReadinessStateValue | null;
	/** Ask field is multi-line, so bottom-align with its last row. */
	multiLine: boolean;
	/** Add mode: per-tab submit label. */
	addLabel: string;
	/** The label's short form, for a row with room for a word but not the noun. */
	addShortLabel: string;
	addCanSubmit: boolean;
	addIsSubmitting: boolean;
	/** Shown as a tooltip while the add submit is disabled, explaining what's still required. */
	addDisabledHint?: string;
	onAddSubmit: () => void;
}

/**
 * The submit control persists across ask and add modes and morphs between them — the round send
 * button widens into the labelled add pill (and back) rather than one swapping for the other. It
 * stays on the right the whole time, so the transition reads as a single element changing shape
 * (which the swap couldn't, since the pill flew in from the wrong side). Width can't transition to
 * `auto`, so the pill width is measured from a hidden label sizer.
 */
const ComposerSubmitButton: React.FC<ComposerSubmitButtonProps> = (props) => {
	const {
		isAdd,
		canSubmitQuery,
		submitTooltip,
		askReadinessState = null,
		multiLine,
		addLabel,
		addShortLabel,
		addCanSubmit,
		addIsSubmitting,
		addDisabledHint,
		onAddSubmit,
	} = props;
	const hasReadinessFailure = askReadinessState ? hasKnowledgeBaseFailure(askReadinessState) : false;
	const readinessStalled = askReadinessState === KnowledgeBaseReadinessState.Stalled;
	const readinessNeedsAttention = hasReadinessFailure || readinessStalled;

	const [pillWidth, setPillWidth] = useState(ASK_DIAMETER_PX);
	/**
	 * The sizer's width is the visible variant's, and the variant follows the row's width, so the
	 * measure has to re-run when the row resizes and not only when the label text changes. A sizer
	 * showing no text at all (the arrow band) measures zero, which is the ask diameter's cue.
	 *
	 * A callback ref rather than an effect over a ref, because the sizer node does not live as long
	 * as the component: Chakra's `loading` re-parents the button's children under its spinner, so a
	 * submit unmounts the sizer and mounts a new one. An observer left on the old node saw it leave
	 * the DOM as a width of zero, wrote the ask diameter, and never heard from the new node; the
	 * next add pill was a circle with its label overhanging. The observer follows the node.
	 */
	const sizerObserverRef = useRef<ResizeObserver | null>(null);
	const sizerRef = useCallback((sizer: HTMLSpanElement | null) => {
		sizerObserverRef.current?.disconnect();
		sizerObserverRef.current = null;
		if (!sizer) return;
		const measure = () => {
			if (!sizer.isConnected) return;
			const width = sizer.offsetWidth;
			setPillWidth(width > 0 ? width + PILL_PADDING_X_PX : ASK_DIAMETER_PX);
		};
		measure();
		if (typeof ResizeObserver === 'undefined') return;
		const observer = new ResizeObserver(measure);
		observer.observe(sizer);
		sizerObserverRef.current = observer;
	}, []);

	// Animate width only on the ask↔add morph — not when a label change re-measures the pill, which
	// would otherwise clip the wider label while the width catches up. `isModeChange` catches the
	// flip frame (before the ref syncs, so the width change animates from the start); `morphing`
	// then holds the transition on for its full duration so re-renders mid-animation don't cut it.
	const prevIsAddRef = useRef(isAdd);
	const prevAddLabelRef = useRef(addLabel);
	const [morphing, setMorphing] = useState(false);
	const morphTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const isModeChange = prevIsAddRef.current !== isAdd;
	const animateWidth = isModeChange || morphing;

	useLayoutEffect(() => {
		const modeChanged = prevIsAddRef.current !== isAdd;
		const labelChanged = prevAddLabelRef.current !== addLabel;
		if (!modeChanged && !labelChanged) return;
		prevIsAddRef.current = isAdd;
		prevAddLabelRef.current = addLabel;
		setMorphing(true);
		if (morphTimerRef.current) clearTimeout(morphTimerRef.current);
		morphTimerRef.current = setTimeout(
			() => setMorphing(false),
			DURATION_FORM_REVEAL_OPEN_MS + COMPOSER_SUBMIT_MORPH_SETTLE_MS,
		);
	}, [addLabel, isAdd]);
	useEffect(
		() => () => {
			if (morphTimerRef.current) clearTimeout(morphTimerRef.current);
		},
		[],
	);

	const disabled = isAdd ? !addCanSubmit || addIsSubmitting : !canSubmitQuery;

	const button = (
		<Button
			type={isAdd ? 'button' : 'submit'}
			onClick={isAdd ? onAddSubmit : undefined}
			aria-label={isAdd ? addLabel : 'Submit query'}
			aria-keyshortcuts={isAdd ? COMPOSER_SUBMIT_KEYSHORTCUTS : undefined}
			loading={isAdd && addIsSubmitting}
			disabled={disabled}
			variant="ghost"
			rounded="full"
			flexShrink={0}
			alignSelf={!isAdd && multiLine ? 'flex-end' : 'center'}
			h={`${ASK_DIAMETER_PX}px`}
			minW={0}
			p={0}
			position="relative"
			overflow="hidden"
			bg={isAdd ? 'brand.solid' : 'transparent'}
			color={isAdd ? 'brand.contrast' : 'brand.fg'}
			_hover={{ bg: isAdd ? 'brand.700' : 'bg.muted', color: isAdd ? 'yellow.500' : 'brand.fg' }}
			_disabled={{ opacity: 0.5, cursor: 'default' }}
			style={{ width: `${isAdd ? pillWidth : ASK_DIAMETER_PX}px` }}
			css={{
				transition: `${animateWidth ? `width ${DURATION_FORM_REVEAL_OPEN_MS}ms ${EASE_OUT}, ` : ''}background-color ${COMPOSER_SUBMIT_CROSSFADE_MS}ms ${EASE_OUT}, color ${COMPOSER_SUBMIT_CROSSFADE_MS}ms ${EASE_OUT}`,
				'@media (prefers-reduced-motion: reduce)': {
					transition: `background-color ${COMPOSER_SUBMIT_REDUCED_MOTION_FADE_MS}ms ease, color ${COMPOSER_SUBMIT_REDUCED_MOTION_FADE_MS}ms ease`,
				},
			}}
		>
			{/* Send icon (ask) and label (add) crossfade, both absolutely centred so neither drives
			    the width — the measured pill width does. */}
			<Box
				as="span"
				position="absolute"
				inset={0}
				display="flex"
				alignItems="center"
				justifyContent="center"
				opacity={isAdd ? 0 : 1}
				transition={`opacity ${COMPOSER_SUBMIT_CROSSFADE_MS}ms ${EASE_OUT}`}
				pointerEvents="none"
			>
				<Icon as={BundleIcon} boxSize={9} />
			</Box>
			<Box
				key={addLabel}
				as="span"
				position="absolute"
				inset={0}
				display="flex"
				alignItems="center"
				justifyContent="center"
				textStyle="body.sm"
				fontWeight="medium"
				whiteSpace="nowrap"
				opacity={isAdd ? 1 : 0}
				transform="translateY(0)"
				pointerEvents="none"
				css={{
					transition: `opacity ${COMPOSER_SUBMIT_CROSSFADE_MS}ms ${EASE_OUT}, transform ${COMPOSER_SUBMIT_CROSSFADE_MS}ms ${EASE_OUT}`,
					'@starting-style': {
						opacity: 0,
						transform: 'translateY(2px)',
					},
					'@media (prefers-reduced-motion: reduce)': {
						transition: `opacity ${COMPOSER_SUBMIT_REDUCED_MOTION_FADE_MS}ms ease`,
					},
				}}
			>
				<Box as="span" css={FULL_LABEL_CSS}>
					{addLabel}
				</Box>
				<Box as="span" css={SHORT_LABEL_CSS}>
					{addShortLabel}
				</Box>
				<Icon as={LuArrowUp} boxSize={5} css={ARROW_CSS} />
			</Box>
			{/* Hidden sizer — measures the label's natural width without affecting layout. */}
			<Box
				as="span"
				ref={sizerRef}
				aria-hidden
				position="absolute"
				visibility="hidden"
				whiteSpace="nowrap"
				textStyle="body.sm"
				fontWeight="medium"
			>
				<Box as="span" css={FULL_LABEL_CSS}>
					{addLabel}
				</Box>
				<Box as="span" css={SHORT_LABEL_CSS}>
					{addShortLabel}
				</Box>
			</Box>
		</Button>
	);

	const control =
		!isAdd && askReadinessState ? (
			<Box
				position="relative"
				display="inline-flex"
				flexShrink={0}
				alignSelf={multiLine ? 'flex-end' : 'center'}
				overflow="visible"
			>
				{button}
				<Box
					as="span"
					aria-hidden
					position="absolute"
					top="-3px"
					right="-3px"
					boxSize="18px"
					display="flex"
					alignItems="center"
					justifyContent="center"
					borderRadius="full"
					bg="surface.default"
					color={hasReadinessFailure ? 'text.error' : readinessStalled ? 'orange.600' : 'brand.fg'}
					zIndex={1}
					pointerEvents="none"
					overflow="hidden"
				>
					<Box position="absolute" inset={0} display="flex" alignItems="center" justifyContent="center">
						<Icon
							as={readinessNeedsAttention ? LuCircleAlert : LuBookOpen}
							boxSize="11px"
							strokeWidth={2.5}
						/>
					</Box>
					{!readinessNeedsAttention && (
						<ShimmerOverlay
							style={
								askReadinessState === KnowledgeBaseReadinessState.Delayed
									? { animationDuration: `${DELAYED_READINESS_SHIMMER_DURATION_MS}ms` }
									: undefined
							}
						/>
					)}
				</Box>
			</Box>
		) : (
			button
		);

	// Add mode: the pill's label is self-evident, so the tip carries what the pill cannot — what is
	// still required while it is disabled, and otherwise the chord that sends it without the mouse.
	// Ask mode: always tip (the round icon isn't labelled).
	const addDisabledTooltip = isAdd && !addCanSubmit && !addIsSubmitting ? addDisabledHint : undefined;
	// Withheld mid-submit: the press has already happened, so naming a way to press it again is
	// noise over a button that is busy.
	const showShortcutTip = isAdd && !addDisabledTooltip && !addIsSubmitting;
	const addTooltip =
		addDisabledTooltip ??
		(showShortcutTip ? (
			<HStack gap={1.5}>
				<KeyCap decorative>{COMPOSER_SUBMIT_SHORTCUT_HINT}</KeyCap>
				{/* The pill's label is a button title — "Add task" — and here it is mid-sentence. */}
				<Box as="span">to {addLabel.toLowerCase()}</Box>
			</HStack>
		) : undefined);

	if (isAdd) {
		return addTooltip ? (
			<Tooltip
				openDelay={COMPOSER_TOOLTIP_OPEN_DELAY_MS}
				closeDelay={COMPOSER_TOOLTIP_CLOSE_DELAY_MS}
				content={addTooltip}
				positioning={{ placement: 'top' }}
				// The cap is gone below `md` with everything else keyboard-shaped, so the tip goes with
				// it rather than opening an empty box on a narrow window.
				contentProps={showShortcutTip ? { display: { base: 'none', md: 'block' } } : undefined}
			>
				{control}
			</Tooltip>
		) : (
			control
		);
	}

	return (
		<Tooltip
			openDelay={COMPOSER_TOOLTIP_OPEN_DELAY_MS}
			closeDelay={COMPOSER_TOOLTIP_CLOSE_DELAY_MS}
			content={submitTooltip}
			positioning={{ placement: 'left' }}
		>
			{control}
		</Tooltip>
	);
};

export default ComposerSubmitButton;
